import type { CreateEventRequest } from '@cibli/shared';
import type { Job } from '@cibli/shared';

import { AnalyticsEvent } from '@cibli/shared';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTrackingContext } from '@/features/common/contexts/tracking.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { createEventsBatch } from '@/services/analytics';

const DEBOUNCE_MS = 1000;

/**
 * Tracks JOB_LISTED when job cards become visible (Intersection Observer).
 * Buffers events and sends in batch after 1s debounce, or immediately on flush (e.g. before opening a job).
 */
export function useJobListedTracking(jobs: Job[]) {
  const { eventSessionId } = useTrackingContext();
  const { user } = useUserContext();
  const { platformType, booth } = useBooth();
  const userId = useMemo(() => user?.id, [user?.id]);
  const boothId = useMemo(() => booth?.id, [booth?.id]);
  const seenIds = useRef<Set<string>>(new Set());
  const buffer = useRef<CreateEventRequest[]>([]);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobsRef = useRef<Job[]>(jobs);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsRef = useRef<(HTMLDivElement | null)[]>([]);
  jobsRef.current = jobs;

  const flush = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const toSend = buffer.current.splice(0, buffer.current.length);
    if (toSend.length > 0 && eventSessionId) {
      if (import.meta.env.DEV) {
        console.debug('[useJobListedTracking] sending batch', toSend.length, 'JOB_LISTED events');
      }
      createEventsBatch(toSend).catch(err =>
        console.error('[useJobListedTracking] batch send failed', err),
      );
    }
  }, [eventSessionId]);

  const scheduleFlush = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(flush, DEBOUNCE_MS);
  }, [flush]);

  useEffect(() => {
    if (!eventSessionId) return;

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const indexStr = (entry.target as HTMLElement).getAttribute('data-job-listed-index');
          if (indexStr === null) continue;
          const i = parseInt(indexStr, 10);
          if (Number.isNaN(i)) continue;
          const job = jobsRef.current[i];
          if (!job || seenIds.current.has(job.id)) continue;

          seenIds.current.add(job.id);
          buffer.current.push({
            name: AnalyticsEvent.JOB_LISTED,
            eventSessionId,
            platformType: platformType ?? undefined,
            userId: userId ?? undefined,
            boothId: boothId ?? undefined,
            data: {
              jobId: job.id,
              jobTitle: job.title ?? null,
              jobClient: job.companyName ?? null,
              jobSource: job.jobSource ?? null,
              jobScore: typeof job.score === 'number' ? job.score : null,
              jobMatchesJob: job.matchesJob ?? null,
              jobMatchesDistance: job.matchesDistance ?? null,
            },
          } as CreateEventRequest);
          scheduleFlush();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.1 },
    );
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [eventSessionId, platformType, userId, boothId, scheduleFlush]);

  const getRef = useCallback((index: number) => {
    return (el: HTMLDivElement | null) => {
      const observer = observerRef.current;
      const prev = elementsRef.current[index];
      if (prev && observer) observer.unobserve(prev);
      elementsRef.current[index] = el;
      if (el) {
        el.setAttribute('data-job-listed-index', String(index));
        if (observer) observer.observe(el);
      }
    };
  }, []);

  return { getRef, flush };
}
